import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { ArrowRight, BookOpen, Heart, Users, Home as HomeIcon, UserCircle, Sparkles, Sun, Star, Book, Flame, Leaf, Quote, PlayCircle, FileText, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useSiteContent } from '../utils/useSiteContent';

const ICON_MAP = { Heart, Users, Home: HomeIcon, UserCircle, Sparkles, Sun, Star, Book, Flame, Leaf };
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const { user } = useAuth();
  const t = useSiteContent('home.');
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [introVideo, setIntroVideo] = useState(null);
  const [verseOfDay, setVerseOfDay] = useState(null);
  const [sanctuaryDismissed, setSanctuaryDismissed] = useState(() => {
    try { return localStorage.getItem('sanctuaryTeaserDismissed') === '1'; } catch { return false; }
  });

  const dismissSanctuary = () => {
    try { localStorage.setItem('sanctuaryTeaserDismissed', '1'); } catch {}
    setSanctuaryDismissed(true);
  };

  const showSanctuaryTeaser = !sanctuaryDismissed && !user?.is_sanctuary_member;

  useEffect(() => {
    loadCategories();
    loadTestimonials();
    loadIntroVideo();
    loadVerseOfDay();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadVerseOfDay = async () => {
    try {
      const { data } = await api.get('/verse-of-day');
      setVerseOfDay(data);
    } catch (error) {
      // silent
    }
  };

  const loadTestimonials = async () => {
    try {
      const { data } = await api.get('/testimonials');
      setTestimonials(data.slice(0, 3));
    } catch (error) {
      // silent
    }
  };

  const loadIntroVideo = async () => {
    try {
      const { data } = await api.get('/intro-video');
      setIntroVideo(data);
    } catch (error) {
      // silent
    }
  };

  const categoryImages = {
    'dating-engagement': 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85',
    'marriage': 'https://images.unsplash.com/photo-1543829969-57899edf981b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxjb3VwbGUlMjBob2xkaW5nJTIwaGFuZHMlMjB3YXJtJTIwc3Vuc2V0fGVufDB8fHx8MTc4NDg1ODI0MHww&ixlib=rb-4.1.0&q=85',
    'family': 'https://images.unsplash.com/photo-1503431153573-96e959f4d9b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjB3YWxraW5nJTIwdG9nZXRoZXIlMjBuYXR1cmV8ZW58MHx8fHwxNzg0ODU4MjQwfDA&ixlib=rb-4.1.0&q=85',
    'friendships': 'https://images.unsplash.com/photo-1582298538104-fe2e74c27f59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwbGF1Z2hpbmclMjBjb2ZmZWV8ZW58MHx8fHwxNzg0ODU4MjQwfDA&ixlib=rb-4.1.0&q=85',
    'personal-struggles': 'https://images.unsplash.com/photo-1579017308347-e53e0d2fc5e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBqb3VybmFsaW5nJTIwcGVhY2VmdWx8ZW58MHx8fHwxNzg0ODU4MjQwfDA&ixlib=rb-4.1.0&q=85'
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <section className="relative overflow-hidden">
        {/* Warm therapy-room photo backdrop */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'saturate(0.9) brightness(1.05)',
          }}
          aria-hidden
        />
        {/* Warm cream overlay for readability + soft therapeutic feel */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(120deg, rgba(253, 251, 247, 0.94) 0%, rgba(243, 239, 231, 0.88) 45%, rgba(253, 251, 247, 0.78) 100%)' }}
          aria-hidden
        />
        <div className="max-w-7xl mx-auto px-6 py-28 relative">
          <div className="max-w-3xl">
            <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="community-notice">
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D' }}>
                {t('hero.wheretonext', 'Where to next? Try —')}
              </span>
              <Link
                to="/prayer-wall"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                style={{ fontFamily: 'Manrope, sans-serif', background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)', color: '#3D2E0A', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 4px 12px rgba(201,155,46,0.35), inset 0 1px 0 rgba(255,255,255,0.5)' }}
                data-testid="community-notice-prayer-wall"
              >
                <span>Prayer Wall</span>
              </Link>
              <Link
                to="/anonymous"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                style={{ fontFamily: 'Manrope, sans-serif', background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)', color: '#3D2E0A', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 4px 12px rgba(201,155,46,0.35), inset 0 1px 0 rgba(255,255,255,0.5)' }}
                data-testid="community-notice-anonymous"
              >
                <span>Share Anonymously</span>
              </Link>
              <Link
                to="/topics"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                style={{ fontFamily: 'Manrope, sans-serif', background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)', color: '#3D2E0A', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 4px 12px rgba(201,155,46,0.35), inset 0 1px 0 rgba(255,255,255,0.5)' }}
                data-testid="community-notice-topics"
              >
                <span>Topic Ideas</span>
              </Link>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }} data-testid="hero-title">
              {t('hero.title', 'Restore Your Relationships Through Faith')}
            </h1>
            <p className="text-lg mb-8" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
              {t('hero.subtitle', 'Find guidance, wisdom, and biblical insight for every relationship in your life. From dating to marriage, family to friendships, discover how to build stronger, more meaningful connections.')}
            </p>
            <Link to="/feelings" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl transition-all duration-300 hover:-translate-y-1" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600, fontSize: '16px' }} data-testid="hero-cta">
              <span>{t('hero.cta', 'Start Your Journey')}</span>
              <ArrowRight className="w-5 h-5" style={{ strokeWidth: 2 }} />
            </Link>
          </div>
        </div>
      </section>

      {showSanctuaryTeaser && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12" data-testid="sanctuary-teaser-section">
          <div
            className="relative overflow-hidden rounded-3xl p-5 sm:p-7"
            style={{
              background: 'linear-gradient(135deg, #FBEFCE 0%, #F5D992 55%, #E8C265 100%)',
              boxShadow: '0 12px 40px rgba(232, 194, 101, 0.25)',
              border: '1px solid rgba(232, 194, 101, 0.5)',
            }}
          >
            <button
              onClick={dismissSanctuary}
              aria-label="Dismiss Sanctuary preview"
              className="absolute top-3 right-3 p-1.5 rounded-full transition-colors hover:bg-white/40"
              style={{ color: '#7A5C1E' }}
              data-testid="sanctuary-teaser-dismiss"
            >
              <X className="w-4 h-4" strokeWidth={2.5} />
            </button>

            {/* Decorative sparkle */}
            <div className="absolute -top-6 -right-6 opacity-30 pointer-events-none" aria-hidden>
              <Sparkles className="w-32 h-32" style={{ color: '#FFFFFF' }} strokeWidth={1.2} />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 relative">
              <div
                className="shrink-0 flex items-center justify-center rounded-2xl"
                style={{ width: 56, height: 56, backgroundColor: 'rgba(255,255,255,0.85)' }}
              >
                <Sparkles className="w-7 h-7" style={{ color: '#B8891A' }} strokeWidth={2} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: 'rgba(255,255,255,0.75)', color: '#7A5C1E' }}
                  >
                    {t('sanctuary_teaser.pill', 'New · Members Only')}
                  </span>
                </div>
                <h3
                  className="text-xl sm:text-2xl font-semibold leading-snug mb-1"
                  style={{ fontFamily: 'Playfair Display, serif', color: '#3D2E0A' }}
                  data-testid="sanctuary-teaser-title"
                >
                  {t('sanctuary_teaser.title', 'Step into The Sanctuary')}
                </h3>
                <p
                  className="text-sm sm:text-base leading-relaxed"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#5C4515' }}
                >
                  {t('sanctuary_teaser.body', 'A private prayer wall, articles inspired by your requests, and a soft place to land — for $6.99/month.')}
                </p>
              </div>

              <Link
                to="/sanctuary"
                className="shrink-0 inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  background: 'linear-gradient(135deg, #2C2724 0%, #4A3F35 100%)',
                  color: '#FBEFCE',
                  fontWeight: 700,
                  fontSize: '14px',
                  letterSpacing: '0.02em',
                  boxShadow: '0 6px 20px rgba(44, 39, 36, 0.25)',
                }}
                data-testid="sanctuary-teaser-cta"
              >
                <span>{t('sanctuary_teaser.cta', 'Take a Look')}</span>
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {verseOfDay && (
        <section className="max-w-3xl mx-auto px-6 pt-12" data-testid="verse-of-day-section">
          <div className="p-6 sm:p-8 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(44, 39, 36, 0.06)', border: '1px solid #F0EBE0' }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, transparent, #E57A44, transparent)' }} />
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44' }}>
                {t('verse.label', 'Verse of the Week')}
              </span>
              <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, transparent, #E57A44, transparent)' }} />
            </div>
            <blockquote className="text-center italic" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', fontSize: '20px', lineHeight: 1.7 }}>
              "{verseOfDay.text}"
            </blockquote>
            <p className="text-center mt-3 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#78A18E', letterSpacing: '0.5px' }}>
              — {verseOfDay.reference}
            </p>
          </div>
        </section>
      )}

      {introVideo && (
        <section className="max-w-5xl mx-auto px-6 py-16" data-testid="intro-video-section">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <PlayCircle className="w-6 h-6" style={{ color: '#E57A44' }} />
              <h2 className="text-3xl sm:text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>{t('intro_video.title', 'A Warm Welcome')}</h2>
            </div>
            <p style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>{t('intro_video.subtitle', 'A personal message from Genesis')}</p>
          </div>
          <div className="rounded-3xl overflow-hidden" style={{ boxShadow: '0 12px 40px rgba(44, 39, 36, 0.15)', backgroundColor: '#000' }}>
            <video
              controls
              preload="metadata"
              playsInline
              poster={introVideo.thumbnail_url ? `${API_URL}${introVideo.thumbnail_url}` : undefined}
              className="w-full h-auto"
              style={{ display: 'block', maxHeight: '70vh' }}
              data-testid="intro-video-player"
            >
              <source src={`${API_URL}/api/intro-video/stream`} type={introVideo.content_type || 'video/mp4'} />
              Your browser does not support the video tag.
            </video>
          </div>
          {introVideo.captions && (
            <details className="mt-4 rounded-2xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="intro-video-transcript">
              <summary className="cursor-pointer px-6 py-4 flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#78A18E', fontWeight: 600 }}>
                <FileText className="w-4 h-4" />
                <span>Read Transcript</span>
              </summary>
              <div className="px-6 pb-6" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.9, whiteSpace: 'pre-wrap' }} data-testid="intro-video-transcript-text">
                {introVideo.captions}
              </div>
            </details>
          )}
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="w-8 h-8" style={{ color: '#E57A44', strokeWidth: 1.5 }} />
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>{t('categories.title', 'Explore by Category')}</h2>
          </div>
          <p className="text-base" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>{t('categories.subtitle', 'Choose a path that resonates with your journey')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category, index) => {
            const IconComp = ICON_MAP[category.icon] || Heart;
            return (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="group relative overflow-hidden rounded-3xl transition-all duration-300 hover:-translate-y-2"
                style={{ boxShadow: '0 8px 30px rgba(44, 39, 36, 0.06)' }}
                data-testid={`category-card-${category.id}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={categoryImages[category.id]}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(44, 39, 36, 0.85), transparent)' }}></div>
                  <div className="absolute top-4 right-4 p-3 rounded-full backdrop-blur-md" style={{ backgroundColor: 'rgba(253, 251, 247, 0.9)' }}>
                    <IconComp className="w-5 h-5" style={{ color: '#E57A44', strokeWidth: 2 }} />
                  </div>
                </div>
                <div className="p-6" style={{ backgroundColor: '#FFFFFF' }}>
                  <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{category.name}</h3>
                  <p className="text-sm mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{category.description}</p>
                  {category.verse_text && (
                    <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E6E2D8' }}>
                      <p className="text-xs italic mb-1" style={{ fontFamily: 'Lora, serif', color: '#78A18E' }}>"{category.verse_text.length > 100 ? category.verse_text.slice(0, 100) + '...' : category.verse_text}"</p>
                      <p className="text-xs font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>— {category.verse_reference}</p>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-20" data-testid="home-testimonials-section">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-3">
              <Quote className="w-7 h-7" style={{ color: '#E57A44', strokeWidth: 1.5 }} />
              <h2 className="text-3xl sm:text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>{t('testimonials.title', 'Restoration Stories')}</h2>
            </div>
            <p style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>{t('testimonials.subtitle', 'Real hearts, real healing')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} className="p-7 rounded-3xl relative" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.05)' }}>
                <Quote className="w-5 h-5 absolute top-5 right-5 opacity-25" style={{ color: '#E57A44' }} />
                <p className="mb-5 text-sm" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.7 }}>
                  {t.story.length > 200 ? t.story.slice(0, 200) + '…' : t.story}
                </p>
                <p style={{ fontFamily: 'Merriweather, serif', color: '#78A18E', fontWeight: 600, fontSize: '14px' }}>
                  — {t.name}{t.partner_name ? ` & ${t.partner_name}` : ''}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/testimonials" className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1" style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44', fontWeight: 600, border: '2px solid #E57A44' }} data-testid="home-testimonials-cta">
              <span>{t('testimonials.cta', 'Read More Stories & Share Yours')}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
